import { Link } from '@inertiajs/react';
import { PublicDemoLink } from '@/components/public/PublicDemoLink';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, staggerContainer, scaleIn } from '@/hooks/use-scroll-animation';
import {
  Target,
  Clock,
  TrendingUp,
  FileText,
  Brain,
  Heart,
  Sparkles,
  ArrowRight,
  Quote,
  CircleX,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const benefits = [
  {
    icon: Target,
    title: 'Sedute più focalizzate',
    description: 'Entri già nel vivo: sai cosa è successo e cosa ha colpito la persona.',
  },
  {
    icon: Clock,
    title: 'Più tempo clinico',
    description: 'Meno recap, più lavoro sul materiale che conta davvero.',
  },
  {
    icon: TrendingUp,
    title: 'Pattern visibili',
    description: 'Situazioni ricorrenti, trigger emotivi, segnali più leggibili nel tempo.',
  },
  {
    icon: FileText,
    title: 'Materiale concreto',
    description: 'Registrazioni precise, emozioni nominate, contesti chiari.',
  },
  {
    icon: Brain,
    title: 'Continuità clinica',
    description: 'La riflessione continua tra una seduta e l’altra, in modo guidato.',
  },
  {
    icon: Heart,
    title: 'Alleanza clinica',
    description: 'La continuità tra le sedute sostiene la relazione clinica.',
  },
];

const problems = [
  {
    before: 'È stata una settimana stressante...',
    after:
      "Martedì, dopo la riunione con il capo, ho sentito un nodo allo stomaco. Ho pensato: 'Non sarò mai abbastanza'.",
  },
  {
    before: 'Mi sono sentito ansioso qualche volta',
    after:
      '3 episodi di ansia questa settimana, tutti legati a situazioni sociali. Pattern: paura del giudizio.',
  },
  {
    before: 'Non mi ricordo bene cosa è successo',
    after:
      "Giovedì sera: momento di insight sulla relazione con mia madre. 'Forse cerco sempre la sua approvazione'.",
  },
];

const PercheEmovia = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Perché Scegliere Emovia"
        description="Più continuità tra le sedute, materiale clinico più chiaro e meno tempo perso a ricostruire gli eventi della settimana."
        path="/perche-emovia"
      />
      <section
        className="relative overflow-hidden py-20 md:py-32 min-h-[90vh] flex items-center bg-cover bg-center"
        style={{ backgroundImage: "url('https://cdn.emovia.com/how.webp')" }}
      >
        <div className="absolute inset-0 bg-orange-dark/60" aria-hidden="true" />
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="relative max-w-3xl mx-auto text-center"
          >
            <motion.span
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="inline-block text-sm font-medium text-white/90 mb-4"
            >
              I benefici
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="font-display text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Perché scegliere Emovia
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-lg text-white/90 leading-relaxed"
            >
              Emovia non cambia il tuo modo di fare terapia.
              <br />
              Ti dà strumenti migliori per lavorare sul materiale
              <br />
              che emerge tra una seduta e l’altra.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-orange-lighter">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <span className="inline-block text-sm font-medium text-primary mb-4 font-accent">
              Capitolo 1
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary font-display mb-6">
              Prima e dopo Emovia
            </h2>
            <p className="text-lg text-muted-foreground">
              Da ricordi sbiaditi a materiale clinico chiaro e utilizzabile.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="grid gap-8 max-w-5xl mx-auto"
          >
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-2 gap-4 bg-card rounded-3xl p-6 md:p-8 border border-border shadow-sm"
              >
                <div className="bg-magenta-lighter rounded-2xl p-6 border border-magenta/50">
                  <div className="flex items-center gap-2 mb-3">
                    <CircleX className="h-4 w-4 text-magenta" />
                    <span className="text-xs font-medium text-magenta uppercase tracking-wide">
                      Prima
                    </span>
                  </div>
                  <p className="text-foreground italic">&quot;{problem.before}&quot;</p>
                </div>
                <div className="bg-blue-lighter rounded-2xl p-6 border border-primary/50">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium text-primary uppercase tracking-wide">
                      Dopo
                    </span>
                  </div>
                  <p className="text-foreground">&quot;{problem.after}&quot;</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <span className="inline-block text-sm font-medium text-primary mb-4 font-accent">
              Capitolo 2
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary font-display mb-6">
              Cosa cambia per te
            </h2>
            <p className="text-lg text-muted-foreground">
              Benefici concreti per lavorare meglio, con più continuità.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {benefits.map((benefit) => (
              <motion.div
                key={benefit.title}
                variants={fadeInUp}
                transition={{ duration: 0.5 }}
                className="bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-md hover:border-orange/20 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-6">
                  <benefit.icon className="w-6 h-6 text-orange" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button size="lg" asChild className="text-base px-8 h-12">
              <PublicDemoLink location="perche_emovia_mid_cta">
                Richiedi una demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </PublicDemoLink>
            </Button>
            <Button variant="outline" size="lg" asChild className="text-base px-8 h-12">
              <Link href="/come-funziona">Scopri come funziona</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-blue-lighter">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block text-sm font-medium text-primary mb-4 font-accent">
              Prova sul campo
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary font-display mb-6">
              Psicologi al centro, sempre
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              Emovia lavora per il professionista: materiale più chiaro, continuità tra le sedute,
              zero interpretazioni automatiche.
            </p>
            <Card className="border-border bg-card shadow-sm text-left max-w-3xl mx-auto">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <img
                    src="https://cdn.emovia.com/img/team/elisa.webp"
                    alt="Dott.ssa Elisa Manca"
                    className="h-16 w-16 rounded-2xl object-cover border border-border shrink-0"
                    width={64}
                    height={64}
                  />
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Quote className="h-6 w-6 text-primary" />
                      <p className="text-sm font-medium text-primary">Dalla co-fondatrice</p>
                    </div>
                    <p className="text-foreground text-lg leading-relaxed mb-4">
                      {'\u201cEmovia non cambia il modo in cui lavori. Ti dà materiale più chiaro su cui lavorare, raccolto quando conta davvero \u2014 tra una seduta e l\u2019altra.\u201d'}
                    </p>
                    <div>
                      <p className="font-semibold text-foreground">Dott.ssa Elisa Manca</p>
                      <p className="text-sm text-muted-foreground">Psicologa, Co-founder Emovia</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={scaleIn}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-orange-dark rounded-3xl p-8 md:p-12 border border-border shadow-sm text-center">
              <Quote className="w-12 h-12 text-orange mx-auto mb-6" />
              <p className="text-xl md:text-2xl font-medium text-orange-light leading-relaxed mb-6">
                &quot;Emovia fa domande, non dà risposte.
                <br />
                Tutto il lavoro clinico rimane tuo.&quot;
              </p>
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-orange" />
                <span className="text-orange-light">
                  L&apos;approccio dell&apos;IA Interrogativa
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-blue-lighter/40">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold text-foreground mb-6">Scopri come può aiutarti</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Richiedi una demo e vedi Emovia in azione. Senza impegno.
            </p>
            <Button size="lg" asChild className="text-base px-8 h-12">
              <PublicDemoLink location="perche_emovia_final_cta">
                Richiedi una demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </PublicDemoLink>
            </Button>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PercheEmovia;
